import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as turf from '@turf/turf';

@Injectable({ providedIn: 'root' })
export class GeoMatchService {
  private cbsaFeatures: any= [];

  private stateFeatures: any = [];

  constructor(private http: HttpClient) {}

  async loadGeoJson(): Promise<void> {
    if (this.cbsaFeatures.length && this.stateFeatures.length) return;

    const [cbsa, state] = await Promise.all([
      this.http.get<any>('assets/geoJson/tl_2024_us_cbsa (1).json').toPromise(),
      this.http.get<any>('assets/geoJson/tl_2024_us_state (1).json').toPromise()
    ]);

    this.cbsaFeatures = (cbsa.features || []).map(f => this.processFeature(f));
    this.stateFeatures = (state.features || []).map(f => this.processFeature(f));
  }

  private processFeature(feature: any): any {
    try {
      const geometry = feature.geometry;
      let turfGeometry: any;

      // Convert geometry to Turf format
      if (geometry.type === 'Polygon') {
        turfGeometry = turf.polygon(geometry.coordinates);
      } else if (geometry.type === 'MultiPolygon') {
        turfGeometry = turf.multiPolygon(geometry.coordinates);
      } else {
        console.warn(`Unsupported geometry type: ${geometry.type}`, feature);
        return feature;
      }

      // Precompute bounding box
      const bbox = turf.bbox(turfGeometry);
      return { ...feature, turfGeometry, bbox };
    } catch (e) {
      console.error('Error processing feature:', feature, e);
      return feature;
    }
  }

  getFipsInsidePolygon(polygonGeoJson: any, userFips: string[]): string[] {
    console.time('getFipsInsidePolygon'); // ⏱️ Start timing

    if (!polygonGeoJson?.coordinates) {
      console.warn('Invalid polygon GeoJSON');
      return [];
    }

    // Parse input polygon
    let inputPolygon: any;
    try {
      inputPolygon = turf.polygon(polygonGeoJson.coordinates);
    } catch (e) {
      console.error('Failed to parse input polygon:', e);
      return [];
    }

    // Precompute input polygon's bounding box
    const inputBBox = turf.bbox(inputPolygon);
    const userFipsSet = new Set(userFips);
    const features = [...this.cbsaFeatures, ...this.stateFeatures];

    const matchedIds = features
      .filter(feature => {
        // Fast checks first
        if (!feature.properties?.GEOID || !userFipsSet.has(feature.properties.GEOID)) return false;
        if (!feature.bbox || !feature.turfGeometry) return false;

        // Bounding box intersection check
        if (!this.bboxIntersects(feature.bbox, inputBBox)) return false;

        // Detailed geometry check
        try {
          return turf.booleanIntersects(feature.turfGeometry, inputPolygon);
        } catch (err) {
          console.warn('Spatial check failed for feature:', feature.properties.GEOID, err);
          return false;
        }
      })
      .map(f => f.properties.GEOID);

      console.timeEnd('getFipsInsidePolygon'); // ⏱️ End timing

    return [...new Set(matchedIds)]; // Deduplicate
  }

  // Fast bounding box intersection check
  private bboxIntersects(bbox1: any, bbox2: any): boolean {
    return !(
      bbox1[0] > bbox2[2] || // feature.minX > input.maxX
      bbox1[2] < bbox2[0] || // feature.maxX < input.minX
      bbox1[1] > bbox2[3] || // feature.minY > input.maxY
      bbox1[3] < bbox2[1]    // feature.maxY < input.minY
    );
  }

  getLocationInfo(lat: number, lng: number): { cbsa?: string, stateFips?: string } {
    if (!this.cbsaFeatures || !this.stateFeatures) {
      console.warn('GeoJSON data not loaded yet!');
      return {};
    }

    const point = turf.point([lng, lat]);

    const cbsa = this.cbsaFeatures.find(f => turf.booleanPointInPolygon(point, f));
    const state = this.stateFeatures.find(f => turf.booleanPointInPolygon(point, f));

    return {
      cbsa: cbsa?.properties?.GEOID || null,
      stateFips: state?.properties?.GEOID || null
    };
  }
}
